import { useCallback, useEffect, useRef, useState } from 'react';
import { usePantheon } from '../providers/PantheonProvider';
import type { WebSocketMessage } from '@pantheon/shared';

interface UseWebSocketOptions {
  sessionId: string | null;
  onMessage?: (message: WebSocketMessage) => void;
}

export function useWebSocket({ sessionId, onMessage }: UseWebSocketOptions) {
  const { wsUrl, authToken } = usePantheon();
  const wsRef = useRef<WebSocket | null>(null);
  const [connected, setConnected] = useState(false);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  const connect = useCallback(() => {
    if (!sessionId || !wsUrl) return;

    const url = `${wsUrl}?sessionId=${encodeURIComponent(sessionId)}&token=${encodeURIComponent(authToken)}`;
    const ws = new WebSocket(url);

    ws.onopen = () => setConnected(true);
    ws.onclose = () => setConnected(false);
    ws.onerror = () => setConnected(false);

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data) as WebSocketMessage;
        onMessageRef.current?.(message);
      } catch {
        // Ignore malformed messages
      }
    };

    wsRef.current = ws;
  }, [sessionId, wsUrl, authToken]);

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
      setConnected(false);
    }
  }, []);

  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  return { connected, disconnect };
}
